export type Platform = 'ios' | 'android'

/**
 * A platform glyph for a download or install button.
 *
 * Filled paths on `currentColor`, so the mark inverts with the button rather than carrying a
 * colour of its own — the same reason the category pictograms stroke with `currentColor`,
 * and what keeps ground rule 4 true of a file made of drawings.
 *
 * **These are generic platform marks, not store badges.** Apple and Google both publish
 * exact badge artwork with rules about size, clear space and wording ("Download on the App
 * Store", "Get it on Google Play"), and those rules apply to the badges — which are for
 * linking to a *store listing*. There is no listing on either store, so these buttons do
 * something else entirely: they install the web app. Using the official badge for that
 * would be wrong twice, in brand terms and in what it promises.
 *
 * If the Foundation ever ships native apps, the badges belong here instead, with their real
 * artwork, and `FooterAppPromo.stores` is already wired for it.
 */
const PATHS: Record<Platform, string> = {
  // An apple with a leaf. Simplified, and deliberately not Apple's own mark.
  ios: 'M16.4 12.7c0-2.4 2-3.6 2.1-3.7-1.1-1.7-2.9-1.9-3.5-1.9-1.5-.1-2.8.9-3.5.9s-1.9-.9-3.1-.9c-1.6 0-3.1.9-3.9 2.4-1.7 2.9-.4 7.2 1.2 9.6.8 1.2 1.7 2.4 3 2.4 1.2 0 1.6-.8 3.1-.8s1.8.8 3 .8 2.1-1.1 2.9-2.3c.9-1.4 1.3-2.7 1.3-2.8-.1 0-2.6-1-2.6-3.7ZM14.1 5.6c.6-.8 1.1-1.9 1-3-.9 0-2 .6-2.7 1.4-.6.7-1.1 1.8-1 2.9 1 .1 2-.5 2.7-1.3Z',
  // A robot head: dome, two eyes, two antennae.
  android:
    'M6.2 9.8h11.6v7.4a1.4 1.4 0 0 1-1.4 1.4H7.6a1.4 1.4 0 0 1-1.4-1.4V9.8ZM8.4 8.4a3.6 3.6 0 0 1 7.2 0H8.4Zm1.3-2.1L8.6 4.6m5.7 1.7 1.1-1.7M3.4 11.2a1.1 1.1 0 0 1 2.2 0v4a1.1 1.1 0 0 1-2.2 0v-4Zm15 0a1.1 1.1 0 0 1 2.2 0v4a1.1 1.1 0 0 1-2.2 0v-4ZM8.5 19.4h2.2v2.1a1.1 1.1 0 0 1-2.2 0v-2.1Zm4.8 0h2.2v2.1a1.1 1.1 0 0 1-2.2 0v-2.1Z',
}

export interface PlatformIconProps {
  platform: Platform
  className?: string
}

export function PlatformIcon({ platform, className }: PlatformIconProps) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      width="24"
      height="24"
      // The button names the platform in words beside it, so the glyph is decoration.
      aria-hidden="true"
      focusable="false"
    >
      <path d={PATHS[platform]} />
    </svg>
  )
}
